import { useCallback, useEffect, useMemo } from "react"

import { App, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { useTitleSectionAction } from "../../Components/TitleSection/TitleSectionContext"
import { superAdminRoleId } from "../../Utils/permission"
import { toApiOrdering, toLookupFilterParams } from "./api/params"
import { AdminReceiptHeader } from "./components/AdminReceiptHeader/AdminReceiptHeader"
import { PageStateOverlay } from "./components/EmptyStates/PageStateOverlay"
import { KpiCards } from "./components/KpiCards"
import ReceiptHeaderActions from "./components/LearnMoreAction"
import { ReceiptLookup } from "./components/ReceiptLookup"
import ProgramSelector from "./components/ReceiptProgramSelector/ReceiptProgramSelector"
import { RECEIPT_PAGE_STATE } from "./constants/receipt-status-mapping"
import { useReceiptBusinessId } from "./hooks/useReceiptBusinessId"
import {
  clearFilters,
  INITIAL_KPI,
  INITIAL_RESULT,
  setProp,
  setSelectedProgram
} from "./redux/receipt.slice"
import {
  getReceiptExport,
  getReceiptKpi,
  getReceiptLookups,
  getReceiptState
} from "./redux/receipt.thunks"

import styles from "./styles.module.less"

const ReceiptPage = ({ usersViewForAdmin }) => {
  const dispatch = useDispatch()
  const { notification } = App.useApp()
  const { t } = useTranslation()
  const { setAction } = useTitleSectionAction()
  const userInfo = useSelector((s) => s.auth?.userInfo)
  const isAdmin = useSelector((s) => s.auth.isAdmin) || false
  const isMobile = useSelector((s) => s.nav.isMobile)
  const isDemo = useSelector((s) => s.auth?.userInfo?.is_demo) || false

  const isAdminPage = isAdmin && !usersViewForAdmin
  // Export = Super Admin only when an admin is viewing (CHA1-1499 BLOCK 3);
  // merchants exporting their own data are unaffected.
  const isSuperAdmin = userInfo?.role?.id === superAdminRoleId
  const canExport = !isAdmin || isSuperAdmin

  const businessId = useReceiptBusinessId(usersViewForAdmin)

  const { pageState, firstEnrollBlocked, selectedProgram, filters } = useSelector((s) => s.receipt)

  const isActive = pageState === RECEIPT_PAGE_STATE.ACTIVE

  const handleGetReceiptState = useCallback(async () => {
    try {
      const res = await dispatch(getReceiptState(businessId)).unwrap()

      // A merchant landing on a non-active state is shown the overlay instead of the
      // dashboard, so the stale KPI/lookup data behind it is cleared. Support switching
      // between accounts keeps whatever the page already loaded until the new account's
      // own data arrives, rather than blanking the screen mid-navigation.
      if (!usersViewForAdmin && res?.page_state !== RECEIPT_PAGE_STATE.ACTIVE) {
        dispatch(setProp({ key: "kpi", value: INITIAL_KPI }))
        dispatch(setProp({ key: "result", value: INITIAL_RESULT }))
      }
    } catch {
      /* error handled in thunk */
    }
  }, [dispatch, businessId, usersViewForAdmin])

  // The KPI cards and the table have to answer about the same rows, so both scopes are
  // resolved once, here. The filter bar's Program wins when it is set to something other than
  // "All" — it is the more specific of the two controls — and otherwise the selected card does.
  // Memoised deliberately: it feeds the dependency list of the callback the lookup table calls
  // from an effect, so a fresh object each render would refetch on every render.
  const lookupFilterParams = useMemo(() => toLookupFilterParams(filters), [filters])
  const channelFilter = lookupFilterParams?.channel
  const programScope = lookupFilterParams?.program || selectedProgram || "all"

  const handleGetReceiptKpi = useCallback(async () => {
    await dispatch(
      getReceiptKpi({ businessId, selectedProgram: programScope, channel: channelFilter })
    ).unwrap()
  }, [businessId, dispatch, programScope, channelFilter])

  const handleChangeFilter = useCallback(
    (patch, type) => {
      if (type === "changeVisible") {
        dispatch(setProp({ key: "visibleFilters", value: patch }))
        return
      }
      dispatch(setProp({ key: "filters", value: patch }))
    },
    [dispatch]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearFilters())
  }, [dispatch])

  const handleChangeColumns = useCallback(
    (cols) => dispatch(setProp({ key: "columns", value: cols })),
    [dispatch]
  )

  const handleChangeViewMode = useCallback(
    (mode) => dispatch(setProp({ key: "viewMode", value: mode })),
    [dispatch]
  )

  const handleSelectProgram = useCallback((id) => dispatch(setSelectedProgram(id)), [dispatch])

  // One Export action, and it means the view: the same filter params and program scope the table
  // is showing go to the server, so the file is the rows on screen (CHA1-1348). The deliberate
  // absence here is "Export all" — a merchant who filtered and then pressed Export has already
  // said what they want. The file is built in the background and mailed, so the answer is an
  // acknowledgement, not a download.
  const handleExport = useCallback(async () => {
    if (!businessId) return

    try {
      const res = await dispatch(
        getReceiptExport({
          business_account_id: businessId,
          ...lookupFilterParams,
          program: programScope
        })
      ).unwrap()

      notification.success({
        message: t("Exporting"),
        description: res?.message
      })
    } catch {
      /* error handled in thunk */
    }
  }, [dispatch, businessId, programScope, lookupFilterParams, t, notification])

  const handleGetLookups = useCallback(
    ({ ordering, ...params } = {}) => {
      if (!businessId) return
      dispatch(
        getReceiptLookups({
          business_account_id: businessId,
          ...lookupFilterParams,
          program: programScope,
          ...params,
          ...(ordering ? { ordering: toApiOrdering(ordering) } : {})
        })
      )
    },
    [dispatch, businessId, lookupFilterParams, programScope]
  )

  // Re-registered when what it shows changes: the header action is held in context, so a stale
  // element would keep the first render's values forever.
  useEffect(() => {
    setAction(<ReceiptHeaderActions isMobile={isMobile} />)
    return () => setAction(null)
  }, [setAction, isMobile])

  return (
    <Flex
      vertical
      gap={24}
      className={styles.page}
      data-testid="deflect-user-page"
      style={usersViewForAdmin ? { paddingTop: 16 } : undefined}
    >
      <AdminReceiptHeader usersViewForAdmin={usersViewForAdmin} />

      {/* §BLOCK 3 */}
      <ProgramSelector
        selectedProgram={selectedProgram}
        getPrograms={handleGetReceiptState}
        onSelect={handleSelectProgram}
      />

      {/* §BLOCK 2 */}
      <KpiCards isActive={isActive} getReceiptKpi={handleGetReceiptKpi} />

      {isActive ? (
        <ReceiptLookup
          isMobile={isMobile}
          isDemo={isDemo}
          isAdminPage={isAdminPage}
          canExport={canExport}
          onChangeFilter={handleChangeFilter}
          onClearFilters={handleClearFilters}
          onChangeColumns={handleChangeColumns}
          onChangeViewMode={handleChangeViewMode}
          onExport={handleExport}
          onGetLookups={handleGetLookups}
        />
      ) : (
        <PageStateOverlay pageState={pageState} firstEnrollBlocked={firstEnrollBlocked} />
      )}
    </Flex>
  )
}

export default ReceiptPage
